import { useState, useCallback, useRef, useEffect, useLayoutEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ComboClearButton } from '@/components/ui/ComboClearButton/ComboClearButton';
import { ContentPasteSearchIcon } from '@/components/ui/Icons';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { ComboEmptyCreatePanel } from '@/components/ui/ComboEmptyCreatePanel/ComboEmptyCreatePanel';
import './SearchSelectField.css';

const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_MIN_SEARCH = 2;

export type SearchSelectAddon = {
  id: string;
  label: React.ReactNode;
  onSelect: () => void;
  className?: string;
  disabled?: boolean;
};

export type SearchSelectAddonContext<T extends { id: string }> = {
  debouncedQuery: string;
  filteredItems: T[];
};

export type SearchSelectFieldProps<T extends { id: string }> = {
  value: T | null;
  onChange: (v: T | null) => void;
  getOptionLabel: (item: T) => string;
  placeholder?: string;
  disabled?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  queryKey: readonly unknown[];
  fetchPage: (debouncedQuery: string, page: number) => Promise<{ items: T[]; hasMore: boolean }>;
  minSearchLength?: number;
  debounceMs?: number;
  staleTime?: number;
  /** Нормализация строки поиска до debounce */
  normalizeQuery?: (raw: string) => string;
  addons?: SearchSelectAddon[];
  /** Доп. пункты списка (создание и т.п.) от текущих результатов поиска */
  computeAddons?: (ctx: SearchSelectAddonContext<T>) => SearchSelectAddon[];
  addonPending?: boolean;
  /** Список без API: при minSearchLength 0 открывать по фокусу и запрашивать fetchPage с пустым запросом */
  allowEmptySearch?: boolean;
  /** Смена ключа (напр. при открытии модалки) — синхронизирует debounce с текущей строкой */
  debounceFlushKey?: string | number;
};

export function SearchSelectField<T extends { id: string }>({
  value,
  onChange,
  getOptionLabel,
  placeholder = 'Поиск…',
  disabled,
  containerRef,
  queryKey,
  fetchPage,
  minSearchLength = DEFAULT_MIN_SEARCH,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  staleTime = 30_000,
  normalizeQuery = (s) => s.trim(),
  addons = [],
  computeAddons,
  addonPending = false,
  allowEmptySearch = false,
  debounceFlushKey,
}: SearchSelectFieldProps<T>) {
  const reactId = useId().replace(/:/g, '');
  const listDomId = `search-select-${reactId}-list`;
  const queryKeySig = JSON.stringify(queryKey);

  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(false);

  const containerRefLocal = useRef<HTMLDivElement>(null);
  const dropdownWrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const caretAfterCommitEditRef = useRef<number | null>(null);
  /** onChange(null) из правки подписи — не затирать строку поиска в эффекте синхронизации */
  const skipClearInputOnNullRef = useRef(false);
  const resolvedContainerRef = containerRef ?? containerRefLocal;
  const dropdownStyle = useDropdownPosition(resolvedContainerRef, open, { dropdownRef: dropdownWrapRef });

  const searchQuery = normalizeQuery(inputValue);
  const debouncedSearch = useDebouncedValue(searchQuery, debounceMs, debounceFlushKey);

  const queryEnabled = !disabled && debouncedSearch.length >= minSearchLength;

  const { data: searchResponse, isFetching, isPlaceholderData } = useQuery({
    queryKey: [...queryKey, debouncedSearch, page],
    queryFn: () => fetchPage(debouncedSearch, page),
    enabled: queryEnabled,
    staleTime,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (!searchResponse || isPlaceholderData) return;
    if (page === 0) {
      setAccumulated(searchResponse.items);
      setHasMore(searchResponse.hasMore);
    } else {
      setAccumulated((prev) => [...prev, ...searchResponse.items]);
      setHasMore(searchResponse.hasMore);
    }
  }, [searchResponse, page, debouncedSearch, isPlaceholderData]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, queryKeySig]);

  useEffect(() => {
    if (value !== null) return;
    if (skipClearInputOnNullRef.current) {
      skipClearInputOnNullRef.current = false;
      return;
    }
    setInputValue('');
  }, [value]);

  useLayoutEffect(() => {
    const pos = caretAfterCommitEditRef.current;
    if (pos == null) return;
    caretAfterCommitEditRef.current = null;
    const el = inputRef.current;
    if (!el) return;
    const clamped = Math.min(Math.max(0, pos), el.value.length);
    el.setSelectionRange(clamped, clamped);
  }, [value, inputValue]);

  const filtered = accumulated.filter((r) => r.id !== value?.id);
  const computed = computeAddons?.({ debouncedQuery: debouncedSearch, filteredItems: filtered }) ?? [];
  const activeAddons = [...addons, ...computed].filter((a) => !a.disabled);
  const optionRows = filtered.length + activeAddons.length;

  const handleLoadMore = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPage((p) => p + 1);
  }, []);

  const selectItem = useCallback(
    (item: T) => {
      onChange(item);
      setInputValue('');
      setOpen(false);
    },
    [onChange]
  );

  useEffect(() => {
    setHighlightIndex(0);
  }, [filtered.length, activeAddons.length]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.children[highlightIndex] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex, open]);

  const hasValue = !!value;

  const showDropdown =
    !disabled && !hasValue && open && (inputValue.trim().length > 0 || allowEmptySearch);

  const canQuery = searchQuery.length >= minSearchLength;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (value) {
      const label = getOptionLabel(value);
      const el = e.currentTarget;
      const rawStart = el.selectionStart;
      const rawEnd = el.selectionEnd;
      const start = rawStart ?? label.length;
      const end = rawEnd ?? label.length;

      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        let next: string;
        let caret: number;
        if (start !== end) {
          next = label.slice(0, start) + label.slice(end);
          caret = start;
        } else if (e.key === 'Backspace') {
          if (start === 0) return;
          next = label.slice(0, start - 1) + label.slice(start);
          caret = start - 1;
        } else {
          if (start >= label.length) return;
          next = label.slice(0, start) + label.slice(start + 1);
          caret = start;
        }
        skipClearInputOnNullRef.current = true;
        onChange(null);
        setInputValue(next);
        caretAfterCommitEditRef.current = Math.min(caret, next.length);
        setOpen(next.trim().length > 0 || allowEmptySearch);
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        const next = label.slice(0, start) + e.key + label.slice(end);
        skipClearInputOnNullRef.current = true;
        onChange(null);
        setInputValue(next);
        caretAfterCommitEditRef.current = Math.min(start + 1, next.length);
        setOpen(next.trim().length > 0 || allowEmptySearch);
        return;
      }
    }

    if (!open) {
      const canOpenKeyboard =
        searchQuery.length >= minSearchLength || (allowEmptySearch && minSearchLength === 0);
      if ((e.key === 'ArrowDown' || e.key === 'Enter') && canOpenKeyboard) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, Math.max(optionRows - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIndex < filtered.length) {
        const opt = filtered[highlightIndex];
        if (opt) selectItem(opt);
      } else {
        const ad = activeAddons[highlightIndex - filtered.length];
        if (ad && !ad.disabled) {
          ad.onSelect();
          setOpen(false);
        }
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleBlur = () => {
    setOpen(false);
  };

  const displayValue = hasValue ? getOptionLabel(value!) : inputValue;
  const loading = isFetching || addonPending;

  const dropdownBody = !canQuery ? (
    <div className="search-select-field__hint">Введите минимум {minSearchLength} символа для поиска</div>
  ) : filtered.length === 0 && loading ? (
    <div className="search-select-field__hint">Загрузка…</div>
  ) : filtered.length === 0 && activeAddons.length > 0 ? (
    <ComboEmptyCreatePanel
      actions={activeAddons.map((ad) => ({
        id: ad.id,
        label: ad.label,
        onSelect: () => {
          ad.onSelect();
          setOpen(false);
        },
        disabled: ad.disabled,
      }))}
    />
  ) : filtered.length === 0 ? (
    <div className="search-select-field__hint">Нет результатов</div>
  ) : (
    <>
      <OverlayScrollArea
        contentRef={listRef}
        element="ul"
        id={listDomId}
        className="search-select-field__dropdown"
        role="listbox"
      >
        {filtered.map((opt, i) => (
          <li
            key={opt.id}
            id={`${listDomId}-opt-${i}`}
            role="option"
            aria-selected={i === highlightIndex}
            className={`search-select-field__option ${i === highlightIndex ? 'search-select-field__option_highlight' : ''}`}
            onMouseDown={(ev) => {
              ev.preventDefault();
              selectItem(opt);
            }}
          >
            {getOptionLabel(opt)}
          </li>
        ))}
      </OverlayScrollArea>
      {hasMore && (
        <button
          type="button"
          className="search-select-field__load-more"
          onMouseDown={(ev) => {
            ev.preventDefault();
            handleLoadMore(ev);
          }}
        >
          <ContentPasteSearchIcon size={18} />
          Загрузить ещё
        </button>
      )}
    </>
  );

  return (
    <div ref={resolvedContainerRef as React.RefObject<HTMLDivElement>} className="search-select-field">
      <div className={`search-select-field__field ${hasValue && !disabled ? 'search-select-field__field_has-clear' : ''}`}>
        <div className="search-select-field__input-wrap">
          <input
            ref={inputRef}
            className={`search-select-field__input ${loading ? 'search-select-field__input_loading' : ''} ${hasValue ? 'search-select-field__input_readonly' : ''}`}
            value={displayValue}
            readOnly={hasValue}
            onChange={(e) => {
              if (disabled || hasValue) return;
              const v = e.target.value;
              setInputValue(v);
              setOpen(v.trim().length > 0 || allowEmptySearch);
            }}
            onFocus={() => {
              if (!disabled && !hasValue && allowEmptySearch) setOpen(true);
            }}
            onPaste={(e) => {
              if (disabled || !value) return;
              e.preventDefault();
              const text = e.clipboardData.getData('text/plain');
              const label = getOptionLabel(value);
              const el = e.currentTarget;
              const start = el.selectionStart ?? label.length;
              const end = el.selectionEnd ?? label.length;
              const next = label.slice(0, start) + text + label.slice(end);
              skipClearInputOnNullRef.current = true;
              onChange(null);
              setInputValue(next);
              caretAfterCommitEditRef.current = Math.min(start + text.length, next.length);
              setOpen(next.trim().length > 0 || allowEmptySearch);
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={disabled ? '' : placeholder}
            disabled={disabled}
            autoComplete="off"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={listDomId}
            aria-activedescendant={open ? `${listDomId}-opt-${highlightIndex}` : undefined}
          />
          {loading && <span className="search-select-field__loading">…</span>}
        </div>
        {hasValue && !disabled && <ComboClearButton onClick={() => onChange(null)} />}
      </div>
      {showDropdown &&
        createPortal(
          <div
            ref={dropdownWrapRef}
            className="search-select-field__dropdown-wrap"
            data-combo-dropdown
            style={dropdownStyle}
          >
            {dropdownBody}
          </div>,
          document.body
        )}
    </div>
  );
}
